<script lang="ts" setup>
  import { useQuery } from '@pinia/colada';
  import { refDebounced } from '@vueuse/core';
  import { computed, ref, watch } from 'vue';
  import { reasonOf } from '../../../../shared/api/client.ts';
  import type { PlayerChoosingView } from '../../../../shared/api/round.ts';
  import { chooseSong as postChosenSong, type Song } from '../../../../shared/api/song.ts';
  import { suggestionsQuery } from '../../../../shared/api/suggestions.ts';
  import { startUserAction, userAction } from '../../../../shared/observability/userAction.ts';
  import ChooserChoosingView from './ChooserChoosingView.vue';

  const props = defineProps<{ data: PlayerChoosingView; secondsLeft: string; isHost: boolean }>();

  const songQuery = ref<string>('');
  const search = refDebounced(songQuery, 200);
  const { data: suggested, error: searchFailure } = useQuery(() => suggestionsQuery(search.value));
  const suggestions = computed(() => suggested.value ?? []);

  const isChoosingLoading = ref(false);

  const chooseSong = async (song: Song) => {
    if (isChoosingLoading.value) return;

    startUserAction(userAction.roundChooseSong, suggestionPick(song));
    isChoosingLoading.value = true;
    try {
      await postChosenSong(song);
    } catch (error) {
      err.value = reasonOf(error);
    } finally {
      isChoosingLoading.value = false;
    }
  };

  const err = ref<string>('');
  const searchErr = ref<string>('');

  const suggestionPick = (song: Song) => ({
    suggestion_count: suggestions.value.length,
    suggestion_index: suggestions.value.indexOf(song),
    round: props.data.round.number,
  });

  watch(songQuery, () => {
    err.value = '';
    searchErr.value = '';
  });

  watch(searchFailure, (failure) => {
    if (failure) searchErr.value = reasonOf(failure);
  });
</script>

<template>
  <ChooserChoosingView
    v-model="songQuery"
    :data="data"
    :error="err"
    :is-host="isHost"
    :search-error="searchErr"
    :is-loading="isChoosingLoading"
    :songs="suggestions"
    :seconds-left="secondsLeft"
    @chosen-song="chooseSong"
  />
</template>
